import React, { FC } from 'react'
import styled from 'styled-components'
import { saveCsvImportUserListErrors } from 'utils/csvUtils'
import { useTranslation } from 'react-i18next'
import { Attachment, GetApp } from '@material-ui/icons'
import { theme, Text, Button } from 'ui'
import { observer } from 'mobx-react'
import useStore from 'store'
import Dropzone from 'react-dropzone'
import { ExportToCsv } from 'utils/export-to-csv'

const EmptyState = styled.div`
    display: flex;
    gap: 20px;
    flex: 1;
    padding: 10px;
    height: 100%;
    align-items: stretch;

    @media (max-width: 900px) {
        flex-direction: column;
        overflow-y: auto;
    }
`
const Instructions = styled.div`
    flex: 0 0 380px;
    max-width: 380px;
    background: ${theme.color.whiteBackground};
    border: 1px solid ${theme.color.lightGrayBackground};
    border-radius: 12px;
    padding: 22px 24px;
    display: flex;
    flex-direction: column;
    text-align: right;

    @media (max-width: 900px) {
        flex: none;
        max-width: none;
    }
`
const InstrTitle = styled.div`
    font-size: 17px;
    font-weight: 700;
    color: ${theme.color.mekomiBlack};
    margin-bottom: 8px;
`
const InstrText = styled.p`
    font-size: 14px;
    color: ${theme.color.base};
    margin: 0 0 12px;
    line-height: 1.55;
`
const ColList = styled.ol`
    margin: 0 0 12px;
    padding-inline-start: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    li {
        font-size: 14px;
        color: ${theme.color.base};
    }
    li b {
        color: ${theme.color.mekomiBlack};
    }
`
const InstrNote = styled.p`
    font-size: 13px;
    color: ${theme.color.grey};
    line-height: 1.55;
    margin: 0 0 18px;
`
const SampleButton = styled(Button)`
    &.MuiButtonBase-root {
        align-self: flex-start;
        text-transform: none;
        font-size: 14px;
        font-weight: 600;
        height: 42px;
        border-radius: 10px;
        padding: 0 18px;
        margin-top: auto;
        background: ${theme.color.white};
        color: ${theme.color.confirmButton};
        border: 1.5px solid ${theme.color.confirmButton};

        &:hover {
            background: ${theme.color.confirmButton}12;
        }
    }
`
const DropWrap = styled.div`
    flex: 1;
    display: flex;
    min-height: 260px;
`
const DropArea = styled.div`
    border-radius: 4px;
    color: rgba(0,0,0,0.6);
    background: #fafafa;
    border: 1px dashed ${theme.color.gray};
    display: flex;
    justify-content:center;
    align-items: center;
    flex:1;
`
const DropAreaContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`
const DropAreaIconsContainer = styled.div`
  display: flex;
  flex-direction: row;
`
const SelectFileButton = styled(Button)`
  &.MuiButtonBase-root {
    margin-top: 14px;
    text-transform: none;
    font-size: 14px;
    font-weight: 600;
    height: 40px;
    border-radius: 10px;
    padding: 0 22px;
    background: ${theme.color.confirmButton};
    color: ${theme.color.white};

    &:hover {
      background: ${theme.color.darkBlue};
    }
  }
`

const DropCsvView = ({ handleUpload }: { handleUpload: (fils: File[]) => void }) => {
    const { t } = useTranslation('members')
    const { uiStore } = useStore()
    return (
        <Dropzone
            onDrop={handleUpload}
            maxFiles={1}
            onDropRejected={(e) => {
                if (e && e[0]?.errors && e[0]?.errors[0]?.code === 'too-many-files') {
                    uiStore.openWarningModal({
                        text: t`fileLimit`,
                        okText: t`app:gotIt`,
                        removeCancelButton: true

                    })
                }
                if (e && e[0]?.errors && e[0]?.errors[0]?.code === 'file-invalid-type') {
                    uiStore.openWarningModal({
                        text: t`MessageDialog:notCSV`,
                        okText: t`app:gotIt`,
                        removeCancelButton: true
                    })
                }

            }}
            multiple={false}
        // accept={['text/csv']}
        >
            {({ getRootProps, getInputProps, open }) => (
                <DropArea {...getRootProps()}>
                    <input {...getInputProps()} />
                    <DropAreaContent>
                        <DropAreaIconsContainer>
                            <Attachment />
                        </DropAreaIconsContainer>
                        <Text align={'center'} textcolor={theme.color.black + 'A1'}>{t`dropCsv`}</Text>
                        <SelectFileButton type="button" onClick={(e) => { e.stopPropagation(); open() }}>{t`choose_file`}</SelectFileButton>
                    </DropAreaContent>
                </DropArea>
            )}
        </Dropzone>
    )
}

const AddCsvScreen: FC = ({ }) => {
    const { t } = useTranslation('members')
    const { uiStore, csvStore } = useStore()

    const downloadSample = () => {
        const headers = ['שם פרטי', 'שם משפחה', 'כינוי', 'מספר תעודת זהות', 'טלפון נייד 1', 'טלפון נייד 2', 'דוא"ל']
        const data = [
            { firstName: 'ישראל', lastName: 'ישראלי', nickName: '', israeliID: '123456789', phone1: '050-1234567', phone2: '', email: 'israel@example.com' },
            { firstName: 'שרה', lastName: 'כהן', nickName: '', israeliID: '987654321', phone1: '052-7654321', phone2: '', email: 'sarah@example.com' },
        ]

        const csvExporter = new ExportToCsv({
            fieldSeparator: ',',
            quoteStrings: '',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: true,
            filename: 'sample_members',
            title: t`sample_title_members`,
            useTextFile: false,
            useBom: true,
            headers,
        })

        csvExporter.generateCsv(data)
    }

    const handleUpload = async (files: File[]) => {
        if (files.length != 1) {
            return
        }

        try {
            const { hasErrors, errors } = await csvStore.setCsvFileUsers(files[0]);

            if (hasErrors) {
                uiStore.openWarningModal({
                    text: t`MessageDialog:errorsList`,
                    secondText: t`export_errors_hint`,
                    okText: t`export_action`,
                    cancelText: t`close`,
                    cancelAction: () => {
                        csvStore.cleanCsvUsers()
                    },
                    okAction: () => {
                        saveCsvImportUserListErrors(errors, t);
                        csvStore.cleanCsvUsers()
                    }
                })
            }
        } catch (error) {
            uiStore.openWarningModal({
                text: t`MessageDialog:notCSV`,
                okText: t`app:gotIt`,
                removeCancelButton: true

            })
            csvStore.cleanCsvUsers()
        }

    }
    return (
        <EmptyState>
            <Instructions>
                <InstrTitle>{t`file_structure`}</InstrTitle>
                <InstrText>{t`file_structure_text`}</InstrText>
                <ColList>
                    <li><b>{t`col_given_name`}</b></li>
                    <li><b>{t`col_family_name`}</b></li>
                    <li>{t`col_nickname`}</li>
                    <li>{t`col_identity`}</li>
                    <li>{t`col_mobile_1`}</li>
                    <li>{t`col_mobile_2`}</li>
                    <li>{t`col_email`}</li>
                </ColList>
                <InstrNote>{t`file_note_members`}</InstrNote>
                <SampleButton onClick={downloadSample} startIcon={<GetApp />}>{t`download_sample`}</SampleButton>
            </Instructions>
            <DropWrap>
                <DropCsvView handleUpload={handleUpload} />
            </DropWrap>
        </EmptyState>
    )
}

export default observer(AddCsvScreen)
